/**
 * On-screen walking controls for a baked Street View tour.
 *
 * Arrows are laid out by real-world bearing relative to where the viewer is
 * currently looking, so "forward" means the street ahead rather than a fixed
 * compass direction. Each arrow corresponds to a panorama that is already in
 * Cloud Storage — the graph is pruned to its own members when it is baked —
 * so a student can never walk into a missing panorama.
 *
 * The teacher gets one extra control. "Gather here" pulls the whole class to
 * the teacher's current panorama, which is the thing that makes free walking
 * usable in a classroom: let them explore, then bring them back to look at the
 * thing you want to talk about.
 */

import { useMemo } from 'react';
import { ArrowUp, Users, MapPin } from 'lucide-react';

import type { WalkGraph } from '../../services/streetViewTourService';

interface StreetViewWalkControlsProps {
  graph: WalkGraph;
  currentPanoId: string;
  /** Where the viewer is looking, in degrees. Arrows rotate relative to this. */
  viewHeading?: number;
  onWalk: (panoId: string) => void;
  /** Teacher only: broadcast this panorama to every student. */
  onGather?: () => void;
  gathering?: boolean;
  walking?: boolean;
}

/** Signed difference between two bearings, in [-180, 180). */
function bearingDelta(from: number, to: number): number {
  return (((to - from + 540) % 360) - 180);
}

export function StreetViewWalkControls({
  graph,
  currentPanoId,
  viewHeading = 0,
  onWalk,
  onGather,
  gathering,
  walking,
}: StreetViewWalkControlsProps) {
  const node = graph.nodes?.[currentPanoId];

  const directions = useMemo(() => {
    if (!node) return [];
    return node.links
      .map((link) => {
        const target = graph.nodes[link.panoId];
        return {
          panoId: link.panoId,
          // Relative to the current view, so the arrow points where the street is.
          relative: bearingDelta(viewHeading, link.heading),
          label: target?.date ? `Imagery ${target.date}` : 'Walk this way',
        };
      })
      .sort((a, b) => Math.abs(a.relative) - Math.abs(b.relative));
  }, [node, graph.nodes, viewHeading]);

  if (!node) return null;

  return (
    <div className="pointer-events-none absolute inset-x-0 bottom-6 flex flex-col items-center gap-3 z-30">
      {directions.length > 0 && (
        <div className="pointer-events-auto flex items-center gap-2 rounded-full bg-black/55 backdrop-blur px-3 py-2 shadow-lg">
          {directions.slice(0, 5).map((d) => (
            <button
              key={d.panoId}
              type="button"
              title={d.label}
              aria-label={`Walk towards bearing ${Math.round(d.relative)} degrees`}
              disabled={walking}
              onClick={() => onWalk(d.panoId)}
              className="w-11 h-11 rounded-full bg-white/15 hover:bg-white/30 disabled:opacity-40 flex items-center justify-center transition-colors"
            >
              <ArrowUp
                className="w-5 h-5 text-white"
                style={{ transform: `rotate(${d.relative}deg)` }}
              />
            </button>
          ))}
        </div>
      )}

      <div className="pointer-events-auto flex items-center gap-2">
        <span className="inline-flex items-center gap-1 rounded-full bg-black/55 backdrop-blur px-2.5 py-1 text-[11px] text-white/80">
          <MapPin className="w-3 h-3" />
          {Object.keys(graph.nodes).length} places to explore
        </span>
        {onGather && (
          <button
            type="button"
            onClick={onGather}
            disabled={gathering}
            className="inline-flex items-center gap-1.5 rounded-full bg-primary/90 hover:bg-primary disabled:opacity-50 px-3 py-1.5 text-xs font-medium text-primary-foreground shadow-lg transition-colors"
          >
            <Users className="w-3.5 h-3.5" />
            {gathering ? 'Gathering…' : 'Gather class here'}
          </button>
        )}
      </div>

      {/* Google requires visible attribution wherever its imagery is shown. */}
      {node.copyright && (
        <span className="pointer-events-none text-[10px] text-white/70 drop-shadow">{node.copyright}</span>
      )}
    </div>
  );
}

export default StreetViewWalkControls;
